import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  AxiosInstance,
  AxiosPaymentInstance,
  AxiosPropertyInstance,
} from "../../../common/utils/AxiosInstance";
import { CheckApiRateLimitError } from "../../../common/utils/functions/helperFunctions";
import CryptoJS from "crypto-js";
import { HMAC_SECRET_KEY } from "../../../config/api.config";

export const getProfile = createAsyncThunk(
  "getProfile",
  async (_, { rejectWithValue }) => {
    const userId = localStorage.getItem("userId");
    const URL = `user/${userId}`;
    const token = JSON.parse(localStorage.getItem("token"));

    try {
      const res = await AxiosInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getProfileReference = createAsyncThunk(
  "getProfileReference",
  async (_, { rejectWithValue }) => {
    const URL = "user/nominee/relations";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.get(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getKYCDocuments = createAsyncThunk(
  "getKYCDocuments",
  async (_, { rejectWithValue }) => {
    const URL = "user/identity/reference";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.get(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const updateProfile = createAsyncThunk(
  "updateProfile",
  async (data, { rejectWithValue }) => {
    const URL = "user";
    const token = JSON.parse(localStorage.getItem("token"));
    // Forms hand over every field they render, so untouched optional ones
    // arrive as "". The API validates them by format (email must be an email),
    // so a blank one fails the whole update — drop them instead of sending.
    const payload = Object.fromEntries(
      Object.entries(data || {}).filter(
        ([, v]) => v !== "" && v !== null && v !== undefined
      )
    );
    const bodyJson = JSON.stringify(payload);
    const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();
    try {
      const res = await AxiosInstance.put(URL, payload, {
        headers: { "x-auth-token": token, "x-signature": hmac },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );

      // Axios errors serialise through toJSON(), which keeps message/config/code
      // but DROPS `response` — so the API's own wording is gone by the time the
      // screen sees this. Lift it out here, while we still hold the real error.
      const apiStatus = error?.response?.data?.status;

      return rejectWithValue({
        ...returnedError,
        apiError:
          apiStatus?.description ||
          apiStatus?.message ||
          error?.message ||
          "Something went wrong. Please try again.",
      });
    }
  }
);

export const setProfilePic = createAsyncThunk(
  "setProfilePic",
  async (data, { rejectWithValue }) => {
    const URL = "user/profile-pic";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.put(URL, data, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );

      return rejectWithValue({ ...returnedError });
    }
  }
);

export const sendUserEmail = createAsyncThunk(
  "sendUserEmail",
  async (data, { rejectWithValue }) => {
    const URL = "user/identity/verify/send-email";
    const token = JSON.parse(localStorage.getItem("token"));
    const bodyJson = JSON.stringify(data);
    const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();
    try {
      const res = await AxiosInstance.post(URL, data, {
        headers: {
          "x-auth-token": token,
          "x-signature": hmac,
        },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({
        ...returnedError,
        status: error?.response?.status,
        message: error?.response?.data?.status?.message || error.message,
      });
      // return rejectWithValue({ ...returnedError });
    }
  }
);

export const verifyUserEmail = createAsyncThunk(
  "verifyUserEmail",
  async (data, { rejectWithValue }) => {
    const URL = "user/identity/verify/verifyEmail";
    const token = JSON.parse(localStorage.getItem("token"));
    const bodyJson = JSON.stringify(data);
    const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();
    try {
      const res = await AxiosInstance.post(URL, data, {
        headers: { "x-auth-token": token, "x-signature": hmac },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return JSON.parse(JSON.stringify(rejectWithValue(error?.response.data)));
    }
  }
);

export const addKyc = createAsyncThunk(
  "addKyc",
  async (data, { rejectWithValue }) => {
    const URL = "/user/identity";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );

      return rejectWithValue({ ...returnedError });
    }
  }
);

export const getKyc = createAsyncThunk(
  "getKyc",
  async (_, { rejectWithValue }) => {
    const URL = "/user/identity";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const shareKyc = createAsyncThunk(
  "shareKyc",
  async (data, { rejectWithValue }) => {
    const URL = `/user/identity/share`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.post(URL, data, {
        headers: { "x-auth-token": token },
      });
      return { respoonse: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );

      return rejectWithValue({ ...returnedError });
    }
    0;
  }
);

export const updateKyc = createAsyncThunk(
  "updateKyc",
  async ({ docID, data }, { rejectWithValue }) => {
    const URL = `/user/identity/${docID}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.put(URL, data, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );

      return rejectWithValue({ ...returnedError });
    }
  }
);

export const verifyPaspport = createAsyncThunk(
  "verifyPaspport",

  async ({ data }, { rejectWithValue }) => {
    const URL = `/user/identity/verify/verifyOtpAadhar`;
    const token = JSON.parse(localStorage.getItem("token"));
    const bodyJson = JSON.stringify(data);
    const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();
    try {
      const res = await AxiosInstance.post(URL, data, {
        headers: {
          "x-auth-token": token,
          "x-signature": hmac,
        },
      });
      //
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const commonKyc = createAsyncThunk(
  "commonKyc",

  async ({ data, id }, { rejectWithValue }) => {
    const URL = `/user/identity/verify/${id}`;

    const token = JSON.parse(localStorage.getItem("token"));

    try {
      const res = await AxiosInstance.post(URL, data, {
        headers: {
          "x-auth-token": token,
        },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const verifyKyc = createAsyncThunk(
  "verifyKyc",

  async ({ id, data }, { rejectWithValue }) => {
    const URL = `/user/identity/verify/${id}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.put(URL, data, {
        headers: { "x-auth-token": token },
      });
      //
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const deleteKyc = createAsyncThunk(
  "deleteKyc",
  async ({ docKey, docID }, { rejectWithValue }) => {
    const URL = `/user/identity/${docKey}`;
    const token = JSON.parse(localStorage.getItem("token"));

    try {
      const res = await AxiosInstance.delete(URL, {
        headers: { "x-auth-token": token },
      });

      return { id: docID, response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

export const setNomineePic = createAsyncThunk(
  "setNomineePic",
  async ({ id, formData }, { rejectWithValue }) => {
    const URL = `/user/nominee/profile-pic`;

    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.post(URL, formData, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

export const createNominee = createAsyncThunk(
  "createNominee",
  async (data, { rejectWithValue }) => {
    const URL = "user/nominee";
    const token = JSON.parse(localStorage.getItem("token"));
    const bodyJson = JSON.stringify(data);
    const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();
    try {
      const res = await AxiosInstance.post(URL, data, {
        headers: {
          "x-auth-token": token,
          "x-signature": hmac,
        },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getNominee = createAsyncThunk(
  "getNominee",
  async (_, { rejectWithValue }) => {
    const URL = "user/nominee";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.get(URL, {
        headers: { "x-auth-token": token },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const updateNominee = createAsyncThunk(
  "updateNominee",
  async ({ ID, data }, { rejectWithValue }) => {
    const URL = `user/nominee/${ID}`;
    const token = JSON.parse(localStorage.getItem("token"));
    const bodyJson = JSON.stringify(data);
    const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();

    try {
      const res = await AxiosInstance.put(URL, data, {
        headers: {
          "x-auth-token": token,
          "x-signature": hmac,
        },
      });

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );

      return rejectWithValue({ ...returnedError });
    }
  }
);

export const getAllAddresses = createAsyncThunk(
  "getAllAddresses",
  async (_, { rejectWithValue }) => {
    const URL = "/user/address/getAll";
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.get(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getAddress = createAsyncThunk(
  "getAddress",
  async ({ ID }, { rejectWithValue }) => {
    const URL = `user/address/${ID}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.get(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const uniqueAddressLabelCheck = createAsyncThunk(
  "uniqueAddressLabelCheck",
  async ({ value }, { rejectWithValue }) => {
    const URL = `user/address/uniquenamecheck?name=${value}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.get(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const addNewAddress = createAsyncThunk(
  "addNewAddress",
  async ({ body }, { rejectWithValue }) => {
    const URL = `user/address`;
    const token = JSON.parse(localStorage.getItem("token"));
    const bodyJson = JSON.stringify(body);
    const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();
    try {
      const res = await AxiosInstance.post(URL, body, {
        headers: {
          "x-auth-token": token,
          "x-signature": hmac,
        },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

export const updateAddress = createAsyncThunk(
  "updateAddress",
  async ({ body, ID }, { rejectWithValue }) => {
    const URL = `user/address/${ID}`;
    const bodyJson = JSON.stringify(body);
    const token = JSON.parse(localStorage.getItem("token"));
    const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();
    try {
      const res = await AxiosInstance.put(URL, body, {
        headers: { "x-auth-token": token, "x-signature": hmac },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

export const deleteAddress = createAsyncThunk(
  "deleteAddress",
  async ({ ID }, { rejectWithValue }) => {
    const URL = `user/address/${ID}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.delete(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      const returnedError = CheckApiRateLimitError(
        error?.response?.status,
        error
      );
      return rejectWithValue({ ...returnedError });
    }
  }
);

export const getCredits = createAsyncThunk(
  "getCredits",
  async ({ _ }, { rejectWithValue }) => {
    const URL = `/credits`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPaymentInstance.get(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const getCreditHistory = createAsyncThunk(
  "getCreditHistory",
  async ({ pagination, body }, { rejectWithValue }) => {
    const URL = `/credits-history?${pagination}`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPaymentInstance.post(URL, body, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const addToWallet = createAsyncThunk(
  "addToWallet",
  async ({ body }, { rejectWithValue }) => {
    const URL = `/v1/customer/checkout/type/ADD_TO_WALLET`;
    // const URL = `/order/credit`
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosPaymentInstance.post(URL, body, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error?.response?.data)));
    }
  }
);

export const contactSupport = createAsyncThunk(
  "contactSupport",
  async ({ body }, { rejectWithValue }) => {
    const URL = `/contact-us/send-message`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.post(URL, body, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const sendAPPlink = createAsyncThunk(
  "sendAPPlink",
  async (data, { rejectWithValue }) => {
    const URL = "/user/send-app-link";
    try {
      const res = await AxiosInstance.post(URL, data);
      return { data, response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return JSON.parse(JSON.stringify(rejectWithValue(error?.response.data)));
    }
  }
);

export const availableServices = createAsyncThunk(
  "availableServices",
  async ({ url }, { rejectWithValue }) => {
    try {
      const res = await AxiosPropertyInstance.get(url);

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  "updateUserStatus",
  async (status, { rejectWithValue }) => {
    const path = `user/change-status/${status}`;

    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.post(
        path,
        {},
        {
          headers: { "x-auth-token": token },
        }
      );

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const updateUserStatusOnTabClose = createAsyncThunk(
  "updateUserStatusOnTabClose",
  async (status, { rejectWithValue }) => {
    const URL = `/user/check/heartbeat`;
    const token = JSON.parse(localStorage.getItem("token"));
    try {
      const res = await AxiosInstance.get(URL, {
        headers: { "x-auth-token": token },
      });
      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);

export const createReferral = createAsyncThunk(
  "createReferral",
  async (_, { rejectWithValue }) => {
    const userId = localStorage.getItem("userId");
    const URL = `user/referral/create`;
    const token = JSON.parse(localStorage.getItem("token"));

    try {
      const res = await AxiosInstance.post(
        URL,
        {},
        {
          headers: { "x-auth-token": token },
        }
      );

      return { response: JSON.parse(JSON.stringify(res)) };
    } catch (error) {
      return rejectWithValue(JSON.parse(JSON.stringify(error)));
    }
  }
);
